// Registry for the hosted RL page (/rl). Static fallback mirrors the server
// catalog in server/rl.go; the page swaps in live data (incl. marked-up GPU
// pricing) from GET /api/rl/environments once loaded.

export interface RLEnvironment {
  id: string;
  name: string;
  category: string;
  categoryLabel: string;
  description: string;
  observation: string;
  action: string;
  algorithms: string[];
  minVramGb: number;
  startReward: number;
  targetReward: number;
}

export interface RLAlgorithm {
  id: string;
  name: string;
  description: string;
  repo: string;
}

export interface RLGpuTier {
  id: string;
  label: string;
  gpu: string;
  vramGb: number;
  priceHourText: string;
  note: string;
}

export interface RLStarterRepo {
  name: string;
  url: string;
  description: string;
  algorithms: string[];
  license: string;
}

export interface RLMetricPoint {
  step: number;
  reward: number;
  loss: number;
  entropy: number;
  stepsPerSec: number;
  gpuUtil: number;
}

export interface RLJob {
  id: string;
  envId: string;
  algoId: string;
  hardware: string;
  status: 'running' | 'succeeded' | 'failed';
  totalSteps: number;
  simulated: boolean;
  progress: number;
  step: number;
  metrics: RLMetricPoint[];
  error?: string;
  deployment?: { kind: string; cogId: string; studioUrl: string; apiUrl: string };
}

export const RL_CATEGORY_LABELS: Record<string, string> = {
  'classic-control': 'Classic control',
  atari: 'Atari',
  mujoco: 'MuJoCo',
  'llm-rlhf': 'LLM RLHF',
  robotics: 'Robotics sim',
  creative: 'Creative systems',
};

export const RL_ENVIRONMENTS: RLEnvironment[] = [
  {
    id: 'cartpole-v1',
    name: 'CartPole-v1',
    category: 'classic-control',
    categoryLabel: RL_CATEGORY_LABELS['classic-control'],
    description: 'Gymnasium classic control: balance a pole on a cart. The hello-world of policy gradients.',
    observation: 'Box(4)',
    action: 'Discrete(2)',
    algorithms: ['ppo', 'dqn'],
    minVramGb: 16,
    startReward: 20,
    targetReward: 500,
  },
  {
    id: 'lunarlander-v3',
    name: 'LunarLander-v3',
    category: 'classic-control',
    categoryLabel: RL_CATEGORY_LABELS['classic-control'],
    description: 'Gymnasium Box2D lander: throttle control with a shaped landing reward.',
    observation: 'Box(8)',
    action: 'Discrete(4)',
    algorithms: ['ppo', 'dqn'],
    minVramGb: 16,
    startReward: -250,
    targetReward: 280,
  },
  {
    id: 'atari-breakout',
    name: 'ALE/Breakout-v5',
    category: 'atari',
    categoryLabel: RL_CATEGORY_LABELS.atari,
    description: 'Atari from pixels with frame stacking; a CNN torso and vectorized envs.',
    observation: 'Box(4,84,84)',
    action: 'Discrete(4)',
    algorithms: ['ppo', 'dqn'],
    minVramGb: 16,
    startReward: 1,
    targetReward: 400,
  },
  {
    id: 'mujoco-halfcheetah',
    name: 'HalfCheetah-v5',
    category: 'mujoco',
    categoryLabel: RL_CATEGORY_LABELS.mujoco,
    description: 'MuJoCo continuous control: 6-joint locomotion, the standard continuous-action benchmark.',
    observation: 'Box(17)',
    action: 'Box(6)',
    algorithms: ['ppo'],
    minVramGb: 16,
    startReward: -300,
    targetReward: 9000,
  },
  {
    id: 'llm-rlhf-tldr',
    name: 'LLM RLHF (TL;DR)',
    category: 'llm-rlhf',
    categoryLabel: RL_CATEGORY_LABELS['llm-rlhf'],
    description: 'GRPO fine-tuning of a small LLM against a programmable reward on summarization prompts.',
    observation: 'prompt tokens',
    action: 'completion tokens',
    algorithms: ['grpo'],
    minVramGb: 24,
    startReward: 0.05,
    targetReward: 0.85,
  },
  {
    id: 'robotics-fetchreach',
    name: 'FetchReach-v3',
    category: 'robotics',
    categoryLabel: RL_CATEGORY_LABELS.robotics,
    description: 'Goal-conditioned robotics sim: move an arm end-effector to a target position.',
    observation: 'Dict(obs, goal)',
    action: 'Box(4)',
    algorithms: ['ppo'],
    minVramGb: 16,
    startReward: -50,
    targetReward: -1.5,
  },
  {
    id: 'appnz-piano-composition-v0',
    name: 'Piano Composition',
    category: 'creative',
    categoryLabel: RL_CATEGORY_LABELS.creative,
    description: 'Compose tonal eighth-note melodies with rewards for harmony, voice leading, rhythmic rests, variety, and cadences.',
    observation: 'MultiDiscrete(13,25,8,5)',
    action: 'Discrete(38)',
    algorithms: ['tabular-q', 'ppo'],
    minVramGb: 0,
    startReward: -20,
    targetReward: 38,
  },
];

export const RL_ALGORITHMS: RLAlgorithm[] = [
  {
    id: 'ppo',
    name: 'PPO',
    description: 'Clipped-surrogate proximal policy optimization with GAE; discrete and continuous actions.',
    repo: 'https://github.com/lee101/appnz-rl-ppo-starter',
  },
  {
    id: 'dqn',
    name: 'DQN',
    description: 'Double DQN with a replay buffer and target network for discrete-action environments.',
    repo: 'https://github.com/lee101/appnz-rl-ppo-starter',
  },
  {
    id: 'grpo',
    name: 'GRPO',
    description: 'Group-relative policy optimization for LLMs: sample groups, normalize rewards, clip, KL-regularize.',
    repo: 'https://github.com/lee101/appnz-rl-rlhf-lite',
  },
  {
    id: 'tabular-q',
    name: 'Tabular Q-learning',
    description: 'Tiny deterministic baseline for symbolic piano composition; emits a compact JSON policy.',
    repo: 'https://github.com/lee101/app-site/tree/master/training/music',
  },
];

// Fallback tiers; live marked-up pricing replaces these from the API.
export const RL_GPU_TIERS: RLGpuTier[] = [
  { id: 'gpu-t4', label: 'T4 16GB', gpu: 'T4', vramGb: 16, priceHourText: '$0.23', note: 'cheapest tier for classic control' },
  { id: 'gpu-rtx4090', label: 'RTX 4090 24GB', gpu: 'RTX-4090', vramGb: 24, priceHourText: '$0.41', note: 'default: fast vectorized envs' },
  { id: 'gpu-rtx5090', label: 'RTX 5090 32GB', gpu: 'RTX-5090', vramGb: 32, priceHourText: '$1.07', note: 'Atari / MuJoCo at scale' },
  { id: 'gpu-l40s', label: 'L40S 48GB', gpu: 'L40S', vramGb: 48, priceHourText: '$1.03', note: 'larger RLHF policies' },
  { id: 'gpu-a100', label: 'A100 80GB', gpu: 'A100-80GB', vramGb: 80, priceHourText: '$1.97', note: 'multi-billion-param RLHF' },
  { id: 'gpu-h100', label: 'H100 80GB', gpu: 'H100-80GB', vramGb: 80, priceHourText: '$3.59', note: 'fastest wall-clock training' },
];

export const RL_STARTER_REPOS: RLStarterRepo[] = [
  {
    name: 'appnz-rl-ppo-starter',
    url: 'https://github.com/lee101/appnz-rl-ppo-starter',
    description:
      'Single-file PPO on gymnasium (classic control, Atari, MuJoCo) with cog.yaml, an app.nz policy server, and a serverless deploy manifest.',
    algorithms: ['ppo', 'dqn'],
    license: 'MIT',
  },
  {
    name: 'appnz-rl-rlhf-lite',
    url: 'https://github.com/lee101/appnz-rl-rlhf-lite',
    description:
      'Minimal GRPO-style RLHF loop on a tiny LLM with programmable rewards, packaged for cog + serverless deploy on app.nz.',
    algorithms: ['grpo'],
    license: 'MIT',
  },
  {
    name: 'appnz-music-rl',
    url: 'https://github.com/lee101/app-site/tree/master/training/music',
    description:
      'Gymnasium-compatible piano composition environment, Q-learning trainer, R2 policy artifact, and Opus Cog inference.',
    algorithms: ['tabular-q', 'ppo'],
    license: 'MIT',
  },
];

// Client-side preview curve for signed-out visitors; matches the server's
// synthetic-metrics shape so the live panel demos without an account.
export function previewMetrics(env: RLEnvironment, totalSteps: number, points: number): RLMetricPoint[] {
  const span = env.targetReward - env.startReward;
  const out: RLMetricPoint[] = [];
  for (let i = 1; i <= points; i++) {
    const f = i / 40;
    const noise = Math.sin(i * 12.9898) * 0.5;
    out.push({
      step: Math.round(f * totalSteps),
      reward: env.startReward + span * (1 - Math.exp(-3 * f)) + noise * Math.abs(span) * 0.03 * (1 - f),
      loss: 1.8 * Math.exp(-2.4 * f) + 0.05 + noise * 0.02,
      entropy: 1.1 * (1 - 0.72 * f) + noise * 0.015,
      stepsPerSec: env.category === 'llm-rlhf' ? 14 + noise * 2 : 2400 + noise * 180,
      gpuUtil: (0.62 + 0.3 * f + noise * 0.04) * 100,
    });
  }
  return out;
}

export const rlEnvById = (id: string): RLEnvironment | undefined => RL_ENVIRONMENTS.find((e) => e.id === id);
export const rlAlgoById = (id: string): RLAlgorithm | undefined => RL_ALGORITHMS.find((a) => a.id === id);
